import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Modal, message as antdMessage } from "antd";
import { CalendarBlank, CalendarPlus, CalendarX, Eye } from "@phosphor-icons/react";
import dayjs from "dayjs";
import {
  confirmDeadlineCardThunk,
  dismissDeadlineCardThunk,
  fetchDeadlineCards,
  selectDeadlineCards,
  selectDeadlineCardsError,
  selectDeadlineCardsStatus,
  selectDeadlineCardsTotal,
} from "../../../slice/InboxSlice";
import ConfirmDeadlineModal from "../../../components/Inbox/ConfirmDeadlineModal";
import DismissDeadlineModal from "../../../components/Inbox/DismissDeadlineModal";
import ReadOnlyDocumentViewer from "../../../components/Document/ReadOnlyDocumentViewer";
import {
  DeadlineActionButton,
  DeadlineCardActions,
  DeadlineCardBox,
  DeadlineCardMeta,
  DeadlineCardTitle,
  DeadlineStripError,
  DeadlineStripHeader,
  DeadlineTabWrapper,
} from "../../Inbox/InboxStyled";

/**
 * Proposed-deadline queue — deadline calendaring's confirmation gate.
 *
 * Lives on the MAIL INTAKE page (Deadlines tab), not the attorney
 * Inbox: confirming deadlines is the mail sorter's job (Matthew,
 * 2026-07-20 — "I'm the lawyer, not the assistant who calendars";
 * superseding the first cut that rendered this as an Inbox strip).
 * One amber card per PROPOSED `DeadlineCard` (VA rating decision /
 * Board decision spotted in the mail, with the computed review
 * deadline). Actions per card:
 *
 *   [View document]  → open the letter in the inbox viewer (URL nav)
 *   [Create event]   → one-click confirm; the BE mirrors a Salesforce
 *                      Event onto the Matter after commit. When the
 *                      card has NO computed deadline (unreadable
 *                      letter date), a date-picker modal collects it —
 *                      the system never guesses a statutory deadline.
 *   [Dismiss]        → reasoned refusal (modal; reason mandatory).
 *
 * As a full tab, an empty queue renders a friendly all-clear panel
 * (the Inbox-strip incarnation rendered nothing). Fetch is owned by
 * the MailIntake page mount; this component is a renderer + action
 * dispatcher.
 */

const formatDate = (value) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : String(value);
};

export const cardHeadline = (card) =>
  `${card?.trigger_label || "Deadline"} — ${card?.matter_name || "Unknown matter"}`;

/**
 * What the empty panel says. "All clear" is only true when the SERVER
 * says the queue is empty. Clearing a capped page empties `cards` while
 * `total` still counts the deadlines behind it, and telling a lawyer
 * there are no deadlines waiting when there are hundreds is the single
 * worst thing this tab could say.
 *
 * `refillDone` marks the case where the next page was already fetched
 * and still came back with nothing visible. Claiming to be loading
 * forever would be its own quiet lie, so that state says plainly that
 * the queue and the page disagree and offers the way out.
 */
export const emptyStateMessage = (status, total, refillDone = false) => {
  if (status === "loading") return "Checking for proposed deadlines…";
  if (total > 0) {
    const one = total === 1;
    const noun = `deadline${one ? "" : "s"}`;
    return refillDone
      ? `${total} ${noun} ${one ? "is" : "are"} still in the queue but ${one ? "it" : "none"} could ${one ? "not " : ""}be loaded. Reload the page to try again.`
      : `Loading the next ${total} ${noun} in the queue…`;
  }
  return "All clear — no deadlines waiting for confirmation.";
};

const DeadlineCardsSection = () => {
  const dispatch = useDispatch();
  const cards = useSelector(selectDeadlineCards);
  const total = useSelector(selectDeadlineCardsTotal);
  const status = useSelector(selectDeadlineCardsStatus);
  const error = useSelector(selectDeadlineCardsError);
  const mutations = useSelector((state) => state.inbox?.deadlineCardMutations);
  // Which card has a modal open:
  // { kind: "view" | "confirm" | "dismiss", card }.
  const [activeModal, setActiveModal] = useState(null);
  // Date picked inside the viewer modal for cards with no computed
  // deadline — the letter STAYS VISIBLE while the sorter reads the
  // date off it (Bugbot #310: switching to the date-picker modal tore
  // down the viewer exactly when it was needed most).
  const [viewerPickedDate, setViewerPickedDate] = useState(null);

  // Confirming or dismissing removes the card locally rather than
  // refetching, which is right until the sorter clears the whole capped
  // page: `items` empties while the queue behind it still has hundreds
  // of deadlines. Pull the next page in that one case. The ref makes it
  // fire once per emptying instead of on every render, so a server that
  // keeps answering with an empty page can't spin.
  const refillRequested = useRef(false);
  const [refillDone, setRefillDone] = useState(false);
  useEffect(() => {
    if (cards.length > 0) {
      refillRequested.current = false;
      setRefillDone(false);
      return;
    }
    if (status !== "ready" || total <= 0) return;
    if (!refillRequested.current) {
      refillRequested.current = true;
      dispatch(fetchDeadlineCards());
      return;
    }
    // The refill landed and the page is still empty. Stop pretending
    // to load; the panel says so instead.
    setRefillDone(true);
  }, [dispatch, cards.length, status, total]);

  const closeModal = useCallback(() => setActiveModal(null), []);

  const runConfirm = useCallback(
    (card, deadlineDate) => {
      dispatch(
        confirmDeadlineCardThunk({ cardId: card.id, deadlineDate })
      )
        .unwrap()
        .then((result) => {
          setActiveModal(null);
          // The Salesforce Event write is flag-gated per environment,
          // and was off in production through July 2026 — this toast
          // promised a calendar event that was never created. Say what
          // actually happened (Bugbot: a false confirmation on a
          // statutory deadline is worse than no confirmation).
          if (result?.card?.calendar_sync_enabled === false) {
            antdMessage.warning(
              "Deadline recorded in DMS — but calendar sync is off, so no event was created on the Matter."
            );
          } else {
            antdMessage.success(
              "Deadline confirmed — the calendar event is being created on the Matter."
            );
          }
        })
        .catch((err) => {
          antdMessage.error(err?.message || "Failed to create the event");
        });
    },
    [dispatch]
  );

  const handleCreateEvent = useCallback(
    (card) => {
      if (card.deadline_date) {
        // One-click path: computed deadline stands unless the human
        // dismisses. No modal (Matthew: "one-click card").
        runConfirm(card, undefined);
      } else {
        setActiveModal({ kind: "confirm", card });
      }
    },
    [runConfirm]
  );

  const handleDismiss = useCallback(
    (card) => setActiveModal({ kind: "dismiss", card }),
    []
  );

  const runDismiss = useCallback(
    ({ reason, note }) => {
      const card = activeModal?.card;
      if (!card) return;
      dispatch(dismissDeadlineCardThunk({ cardId: card.id, reason, note }))
        .unwrap()
        .then(() => {
          setActiveModal(null);
          antdMessage.success("Deadline dismissed — decision recorded.");
        })
        .catch((err) => {
          antdMessage.error(err?.message || "Failed to dismiss the deadline");
        });
    },
    [dispatch, activeModal]
  );

  // A fetch error is shown loudly — a hidden failure here means a
  // statutory deadline could silently wait, the exact failure mode the
  // deadline system exists to prevent. When a refetch fails AFTER
  // cards loaded, the stale cards stay actionable underneath the
  // banner (Bugbot #304 — dropping them would hide their confirm/
  // dismiss buttons even though Redux still holds them). An empty
  // queue renders an explicit all-clear panel — this is a full tab,
  // not the old Inbox strip that vanished when empty.
  const showError = status === "failed" && Boolean(error);
  if (!cards.length && !showError) {
    return (
      <DeadlineTabWrapper aria-label="Proposed deadlines">
        <DeadlineStripHeader>
          <CalendarBlank size={14} weight="bold" />
          Proposed deadlines
        </DeadlineStripHeader>
        <DeadlineCardBox as="div" role="status">
          <DeadlineCardMeta style={{ marginBottom: 0 }}>
            {emptyStateMessage(status, total, refillDone)}
          </DeadlineCardMeta>
        </DeadlineCardBox>
      </DeadlineTabWrapper>
    );
  }

  return (
    <DeadlineTabWrapper aria-label="Proposed deadlines">
      {showError && (
        <DeadlineStripError role="alert">
          Couldn't load proposed deadlines: {error}
        </DeadlineStripError>
      )}
      {cards.length > 0 && (
        <DeadlineStripHeader>
          <CalendarBlank size={14} weight="bold" />
          {/* The server returns a capped page of the soonest deadlines,
              so the header reports the queue's real size and says when
              it is showing only part of it. Counting the rows on screen
              made a 709-deadline queue read as "100". */}
          {total > cards.length
            ? `Proposed deadlines — showing ${cards.length} of ${total}, soonest first`
            : `Proposed deadlines (${cards.length})`}
        </DeadlineStripHeader>
      )}
      {cards.map((card) => {
        const busy = mutations?.[String(card.id)] === "loading";
        const letterDate = formatDate(card.letter_date);
        const deadlineDate = formatDate(card.deadline_date);
        return (
          <DeadlineCardBox key={card.id} data-testid={`deadline-card-${card.id}`}>
            <DeadlineCardTitle>{cardHeadline(card)}</DeadlineCardTitle>
            <DeadlineCardMeta>
              <span>
                Letter date: <strong>{letterDate || "not detected"}</strong>
              </span>
              <span>
                Deadline:{" "}
                <strong>{deadlineDate || "needs a date"}</strong>
              </span>
              {/* Whose desk the deadline lands on. A sorter working the
                  firm-wide queue (deadlines.view_all) sees everyone's
                  cards, so each one must say who it is for. */}
              {card.assigned_to?.name && (
                <span>
                  For: <strong>{card.assigned_to.name}</strong>
                </span>
              )}
              {card.document_name && <span>{card.document_name}</span>}
            </DeadlineCardMeta>
            <DeadlineCardActions>
              <DeadlineActionButton
                type="button"
                $variant="ghost"
                onClick={() => {
                  setViewerPickedDate(null);
                  setActiveModal({ kind: "view", card });
                }}
                disabled={!card.document_id}
              >
                <Eye size={13} weight="bold" />
                View document
              </DeadlineActionButton>
              <DeadlineActionButton
                type="button"
                $variant="primary"
                onClick={() => handleCreateEvent(card)}
                disabled={busy}
              >
                <CalendarPlus size={13} weight="bold" />
                {busy ? "Creating…" : "Create event"}
              </DeadlineActionButton>
              <DeadlineActionButton
                type="button"
                $variant="danger"
                onClick={() => handleDismiss(card)}
                disabled={busy}
              >
                <CalendarX size={13} weight="bold" />
                Dismiss
              </DeadlineActionButton>
            </DeadlineCardActions>
          </DeadlineCardBox>
        );
      })}

      {/* In-place letter preview WITH the decision buttons — the
          sorter reads the letter and calendars it in one spot
          (Matthew, 2026-07-21: "I can't calendar it from here" after
          View document navigated away to the inbox). */}
      <Modal
        open={activeModal?.kind === "view"}
        onCancel={closeModal}
        width="82vw"
        style={{ top: 24 }}
        title={activeModal?.card ? cardHeadline(activeModal.card) : ""}
        destroyOnClose
        footer={
          activeModal?.card ? (
            <DeadlineCardActions style={{ justifyContent: "flex-end" }}>
              <DeadlineActionButton
                type="button"
                $variant="danger"
                disabled={
                  mutations?.[String(activeModal.card.id)] === "loading"
                }
                onClick={() =>
                  // Overlay the reason dialog ON TOP of the viewer —
                  // replacing the modal kind unmounted the letter
                  // exactly when "Not actually a decision" needs it
                  // visible (Bugbot #310 round 2).
                  setActiveModal((prev) => ({ ...prev, overlay: "dismiss" }))
                }
              >
                <CalendarX size={13} weight="bold" />
                Dismiss
              </DeadlineActionButton>
              {!activeModal.card.deadline_date && (
                <DatePicker
                  aria-label="Deadline date"
                  format="YYYY-MM-DD"
                  value={viewerPickedDate}
                  onChange={setViewerPickedDate}
                />
              )}
              <DeadlineActionButton
                type="button"
                $variant="primary"
                disabled={
                  mutations?.[String(activeModal.card.id)] === "loading" ||
                  (!activeModal.card.deadline_date && !viewerPickedDate)
                }
                onClick={() =>
                  activeModal.card.deadline_date
                    ? handleCreateEvent(activeModal.card)
                    : runConfirm(
                        activeModal.card,
                        viewerPickedDate.format("YYYY-MM-DD")
                      )
                }
              >
                <CalendarPlus size={13} weight="bold" />
                {mutations?.[String(activeModal.card.id)] === "loading"
                  ? "Creating…"
                  : `Create event${
                      activeModal.card.deadline_date
                        ? ` — due ${formatDate(activeModal.card.deadline_date)}`
                        : ""
                    }`}
              </DeadlineActionButton>
            </DeadlineCardActions>
          ) : null
        }
      >
        {activeModal?.card && (
          <div style={{ height: "72vh" }}>
            <ReadOnlyDocumentViewer
              key={activeModal.card.document_id}
              documentId={activeModal.card.document_id}
              filename={activeModal.card.document_name || "letter.pdf"}
              fileType="pdf"
            />
          </div>
        )}
      </Modal>

      <ConfirmDeadlineModal
        open={activeModal?.kind === "confirm"}
        cardTitle={activeModal?.card ? cardHeadline(activeModal.card) : ""}
        onClose={closeModal}
        onConfirm={(date) => runConfirm(activeModal.card, date)}
        busy={
          activeModal?.card
            ? mutations?.[String(activeModal.card.id)] === "loading"
            : false
        }
      />
      <DismissDeadlineModal
        open={
          activeModal?.kind === "dismiss" || activeModal?.overlay === "dismiss"
        }
        cardTitle={activeModal?.card ? cardHeadline(activeModal.card) : ""}
        onClose={() =>
          activeModal?.overlay
            ? setActiveModal({ kind: "view", card: activeModal.card })
            : closeModal()
        }
        onConfirm={runDismiss}
        busy={
          activeModal?.card
            ? mutations?.[String(activeModal.card.id)] === "loading"
            : false
        }
      />
    </DeadlineTabWrapper>
  );
};

export default DeadlineCardsSection;
